/**
 * Application Process 5011 — KUK admission process.
 *
 * Numbered steps beside the site's inline lead form, with a full-width CTA
 * standing in for that form below 1080px. Ported from kuk-campus-portal's
 * `components/Degree/AdmissionProcess.tsx`, which hardcodes all five steps twice
 * over; here they are editable rows.
 *
 * Every field maps to a column that already exists on `application_process` —
 * the variant adds no schema of its own beyond widening
 * `application_process.variant`. The lead-form switch rides on `layout_variant`
 * because that table has no `enable_lead_form` column and this is the field that
 * already means "which layout".
 */

export const APPLICATION_PROCESS_5011_CONSTANTS = {
  METADATA_TITLES: {
    metadata_1: "Steps",
  },

  DEFAULT_ROW_1: {
    id: "1",
    meta_icon: "",
    meta_title: "",
    meta_description: "",
    meta_priority: 1,
    meta_status: true,
  },

  APPLICATION_PROCESS_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "Admission Process",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Admission Process",
      required: false,
      defaultValue: "Admission Process",
    },
    {
      fieldName: "title_start_color",
      label: "Heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "layout_variant",
      label: "Layout",
      value: "with_lead_form",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select layout",
      description: "The lead form uses the university's configured variant and never renders below 1080px.",
      required: true,
      defaultValue: "with_lead_form",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Steps + inline lead form", value: "with_lead_form" },
        { label: "Steps only (full width)", value: "steps_only" },
      ],
    },
    {
      fieldName: "desktop_background_color",
      label: "Section background",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F5F5F5",
      required: false,
      defaultValue: "#F5F5F5",
    },
    {
      fieldName: "background_image",
      label: "Section background image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Optional",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "alt_background_image",
      label: "Background image alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "accent_color",
      label: "Step icon colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "Applies to icon-key icons; uploaded images keep their own colours.",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "gradient_color_1",
      label: "Connector line colour",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      description: "The vertical rule between steps.",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "subtitle_1_color",
      label: "Step label colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "subtitle_2_color",
      label: "Step description colour",
      value: "#4B4B4B",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#4B4B4B",
      required: false,
      defaultValue: "#4B4B4B",
    },
    {
      fieldName: "cta_label",
      label: "Mobile CTA text",
      value: "Enroll Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enroll Now",
      description: "Shown below 1080px, where the lead form cannot render.",
      required: false,
      defaultValue: "Enroll Now",
    },
    {
      fieldName: "cta_is_cta",
      label: "CTA opens the lead form popup",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "On = open the popup, off = follow the link below",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "cta_link",
      label: "CTA link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://…",
      description: "Used only when the toggle above is off.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "box_start_color",
      label: "CTA background colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "box_end_color",
      label: "CTA text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [
    {
      header: "Icon",
      fieldName: "meta_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      description: "Accepts an uploaded image URL OR a built-in icon name such as note, send, upload, verified.",
      defaultValue: "",
    },
    {
      header: "Step label",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Step 1",
      description: "Left empty, the step number is taken from its position.",
      defaultValue: "",
    },
    {
      header: "Step description",
      fieldName: "meta_description",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 3,
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
